import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import DateSelect from "@/components/DateSelect";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import Table, { TableControls, TableFooter, type Row } from "@/components/Table";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import usePagination from "@/hooks/usePagination";
import { useRowHighlight } from "@/hooks/useRowHighlight";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import { Path, useNavigate } from "@/router";
import { committeeHistoryHighlightId } from "../committeeHistoryHighlight";
import { COMMITTEES_QUERY } from "../queries";
import CommitteeParticipantsPanel from "./CommitteeParticipantsPanel";
import CommitteeTermsPanel from "./CommitteeTermsPanel";
import {
  getAllColumns,
  canViewCommitteeParticipant,
  getCommitteeParticipantDetailsColumns,
  getCommitteeTermDetailsColumns,
  getTermColumns
} from "./TableColumns";
import type {
  CommitteeDateRangeInput,
  CommitteeNode,
  CommitteeOrderByInput,
  CommitteeParticipantNode,
  CommitteeParticipantWithCommittee,
  CommitteeRow,
  CommitteeSortField,
  CommitteeStatus,
  CommitteeTab,
  CommitteeTermNode,
  CommitteesQueryResponse,
  CommitteesQueryVariables
} from "../types";

const DATE_RANGE_PARAM = "committeeDateRange";
const TERM_SORT_FIELD_MAP: Record<string, CommitteeSortField> = {
  committeeName: "NAME",
  participants: "PARTICIPANT_COUNT",
  startDate: "START_DATE",
  endDate: "END_DATE",
  status: "STATUS"
};

const ALL_SORT_FIELD_MAP: Record<string, CommitteeSortField> = {
  committeeName: "NAME",
  historicalTerms: "TERM_COUNT",
  historicalParticipations: "TOTAL_PARTICIPATION_COUNT",
  latestEndDate: "LATEST_END_DATE",
  status: "STATUS"
};

function tabStatus(tab: CommitteeTab): CommitteeStatus | undefined {
  if (tab === "all") return undefined;
  return tab === "active" ? "ACTIVE" : "INACTIVE";
}

function latestEndDate(committee: CommitteeNode): string | null {
  return sortTermsByMostRecentEndDate(committee.terms)[0]?.endDate ?? null;
}

function sortTermsByMostRecentEndDate(terms: CommitteeNode["terms"]) {
  return [...terms].sort((left, right) => {
    const leftEndDate = left.endDate ?? "9999-12-31";
    const rightEndDate = right.endDate ?? "9999-12-31";

    return rightEndDate.localeCompare(leftEndDate);
  });
}

function committeeCatalogIdFromTerm(term: CommitteeTermNode): string {
  const [type, code] = term.id.split(":");

  return `${type}:${code}`;
}

function formatRows(committees: CommitteeNode[], tab: CommitteeTab): CommitteeRow[] {
  const status = tabStatus(tab);
  if (!status) {
    return committees.map((committee) => ({
      id: committee.id,
      committeeId: committee.id,
      committeeName: committee.name,
      historicalTerms: committee.termCount,
      terms: sortTermsByMostRecentEndDate(committee.terms),
      historicalParticipations: committee.totalParticipationCount,
      endDate: null,
      latestEndDate: latestEndDate(committee),
      status: committee.status
    }));
  }

  return committees.flatMap((committee) =>
    committee.terms
      .filter((term) => term.status === status)
      .map((term) => {
        const committeeId = committeeCatalogIdFromTerm(term);

        return {
          id: `${committeeId}:${term.id}`,
          committeeId,
          termId: term.id,
          committeeName: committee.name,
          participants: term.participantCount,
          startDate: term.startDate,
          endDate: term.endDate,
          status: term.status
        };
      })
  );
}

export function parseDateRange(value: unknown): CommitteeDateRangeInput | undefined {
  if (typeof value !== "string") return undefined;

  const [startsOn, endsOn] = value.split(",");
  if (!startsOn && !endsOn) return undefined;

  return {
    ...(startsOn && { startsOn }),
    ...(endsOn && { endsOn })
  };
}

export function serializeDateRangeParam(startDate: string | null, endDate: string | null) {
  return `${startDate ?? ""},${endDate ?? ""}`;
}

function participantPgaId(participant: CommitteeParticipantNode) {
  return participant.person?.pgaId ?? participant.pgaId;
}

function participantCommitteeHistoryHighlightId(participant: CommitteeParticipantWithCommittee) {
  return committeeHistoryHighlightId({
    committeeName: participant.committeeName,
    position: participant.position,
    startDate: participant.startDate,
    endDate: participant.endDate
  });
}

function toDateParam(value: Date | string | null | undefined) {
  if (!value) return null;
  if (typeof value === "string") return value.split("T")[0];
  return value.toISOString().split("T")[0];
}

function isDateRangeValue(value: unknown): value is { startDate: Date | string | null; endDate: Date | string | null } {
  return typeof value === "object" && value !== null && "startDate" in value && "endDate" in value;
}

export default function CommitteesTable({ activeTab }: { activeTab: CommitteeTab }) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<Record<string, string | null>>({});
  const [selectedParticipantPgaId, setSelectedParticipantPgaId] = useState<string | null>(null);
  const [selectedParticipantHighlightRowId, setSelectedParticipantHighlightRowId] = useState<string | null>(null);
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { isCompactLayout } = useIsCompactLayout();
  const navigate = useNavigate();
  const { params, addSearchParam, deleteSearchParams } = useParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "committeeSearch"
  });

  const supportsDateRangeFilter = activeTab !== "all";
  const dateRange = useMemo(() => parseDateRange(params[DATE_RANGE_PARAM]), [params]);
  const appliedDateRange = supportsDateRangeFilter ? dateRange : undefined;
  const status = tabStatus(activeTab);

  useEffect(() => {
    resetPagination();
  }, [activeTab, debouncedSearch, appliedDateRange?.startsOn, appliedDateRange?.endsOn, resetPagination]);

  useEffect(() => {
    setSorting([]);
    setExpanded({});
  }, [activeTab]);

  const handleExpand = useCallback((rowId: string, panelId: string | null) => {
    setExpanded((currentExpanded) => ({
      ...currentExpanded,
      [rowId]: panelId
    }));
  }, []);

  const handleViewParticipant = useCallback((participant: CommitteeParticipantWithCommittee) => {
    if (!canViewCommitteeParticipant(participant)) return;

    setSelectedParticipantPgaId(participantPgaId(participant));
    setSelectedParticipantHighlightRowId(participantCommitteeHistoryHighlightId(participant));
  }, []);

  const orderBy = useMemo((): CommitteeOrderByInput | undefined => {
    const sort = sorting[0];
    if (!sort) return undefined;

    const field = (activeTab === "all" ? ALL_SORT_FIELD_MAP : TERM_SORT_FIELD_MAP)[sort.id];
    if (!field) return undefined;

    return {
      field,
      direction: sort.desc ? "DESC" : "ASC"
    };
  }, [activeTab, sorting]);

  const variables: CommitteesQueryVariables = {
    ...paginationVariables,
    input: {
      search: debouncedSearch || undefined,
      ...(status && { statuses: [status] }),
      types: ["NATIONAL"],
      dateRange: appliedDateRange
    },
    orderBy
  };

  const { data, previousData, loading } = useQuery<CommitteesQueryResponse, CommitteesQueryVariables>(
    COMMITTEES_QUERY,
    {
      variables
    }
  );

  const connection = data?.committees ?? previousData?.committees;
  const rows = formatRows(connection?.nodes ?? [], activeTab);
  const columns = useMemo(
    () => (activeTab === "all" ? getAllColumns({ expanded, onExpand: handleExpand }) : getTermColumns()),
    [activeTab, expanded, handleExpand]
  );
  const termDetailsColumns = useMemo(() => getCommitteeTermDetailsColumns(), []);
  const participantDetailsColumns = useMemo(
    () => getCommitteeParticipantDetailsColumns(isCompactLayout, handleViewParticipant),
    [handleViewParticipant, isCompactLayout]
  );
  const { highlightRowId, containerRef } = useRowHighlight({
    dataReady: !loading && rows.length > 0
  });
  const hasActiveFilters = !!debouncedSearch || !!appliedDateRange;
  const activeFilterCount = [appliedDateRange].filter(Boolean).length;
  const totalRowCount = connection?.totalCount ?? rows.length;

  const handleDateRangeChange = (value: unknown) => {
    if (!isDateRangeValue(value)) return;

    const startDate = toDateParam(value.startDate);
    const endDate = toDateParam(value.endDate);

    if (startDate || endDate) {
      addSearchParam(DATE_RANGE_PARAM, serializeDateRangeParam(startDate, endDate));
    } else {
      deleteSearchParams(DATE_RANGE_PARAM);
    }

    resetPagination();
  };

  const handleClearAllFilters = () => {
    clearSearch();
    deleteSearchParams(DATE_RANGE_PARAM);
    resetPagination();
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((currentSorting) =>
      typeof updaterOrValue === "function" ? updaterOrValue(currentSorting) : updaterOrValue
    );
    resetPagination();
  };

  const renderParticipantsPanel = useCallback(
    (row: CommitteeRow, participantCount: number) => (
      <CommitteeParticipantsPanel
        committeeId={row.committeeId}
        committeeName={row.committeeName}
        termId={row.termId}
        participantCount={participantCount}
        columns={participantDetailsColumns}
        onViewParticipant={handleViewParticipant}
      />
    ),
    [handleViewParticipant, participantDetailsColumns]
  );

  const renderTermsPanel = useCallback(
    (terms: CommitteeNode["terms"]) => <CommitteeTermsPanel terms={terms} columns={termDetailsColumns} />,
    [termDetailsColumns]
  );

  const renderExpandedRow = useCallback(
    ({ row }: { row: Row<CommitteeRow> }) => {
      const terms = row.original.terms ?? [];

      if (activeTab !== "all") {
        return renderParticipantsPanel(row.original, row.original.participants ?? 0);
      }

      if (isCompactLayout) {
        return (
          <>
            {!!terms.length && renderTermsPanel(terms)}
            {!!row.original.historicalParticipations &&
              renderParticipantsPanel(row.original, row.original.historicalParticipations)}
          </>
        );
      }

      if (expanded[row.id] === "terms") return renderTermsPanel(terms);
      if (expanded[row.id] === "participants") {
        return renderParticipantsPanel(row.original, row.original.historicalParticipations ?? 0);
      }

      return <></>;
    },
    [activeTab, expanded, isCompactLayout, renderParticipantsPanel, renderTermsPanel]
  );

  return (
    <>
      <div ref={containerRef}>
        <Table
          data={rows}
          columns={columns}
          loading={loading}
          sortingState={sorting}
          onSortingChange={handleSortingChange}
          serverSideSorting
          getRowCanExpand={(row) => {
            if (activeTab === "all") {
              return !!row.original.terms?.length || !!(row.original.historicalParticipations ?? 0);
            }
            return !!(row.original.participants ?? 0);
          }}
          renderExpandedRow={renderExpandedRow}
          data-testid={`${activeTab}-committees-table`}
          getRowClasses={
            highlightRowId
              ? (row) =>
                  row.original.id === highlightRowId || row.original.committeeName === highlightRowId
                    ? "row-highlight"
                    : ""
              : undefined
          }
          hasActiveFilters={hasActiveFilters}
          renderNoDataEmptyState={() => (
            <EmptyState
              title={
                activeTab === "all"
                  ? "No Committees"
                  : `No ${activeTab === "active" ? "Active" : "Inactive"} Committees`
              }
              caption={
                activeTab === "all"
                  ? "Committees will appear here when they are available."
                  : "Committee terms will appear here when they are available."
              }
              iconName="groups"
            />
          )}
          renderControls={(_table, { onSortClick } = {}) => (
            <TableControls
              pagination={createHeaderPagination(connection, {
                loading,
                pageCount: rows.length,
                totalCount: totalRowCount
              })}
              onSortClick={onSortClick}
              useExpandedFilters
              expandedFiltersState={{ dates: !!appliedDateRange }}
              activeFilters={activeFilterCount}
              onClearAllFilters={handleClearAllFilters}
            >
              <SearchInput
                query={searchValue}
                onChange={(value) => {
                  setSearchValue(value);
                  resetPagination();
                }}
                onClear={() => {
                  clearSearch();
                  resetPagination();
                }}
                placeholder="Search Committees"
              />
              {supportsDateRangeFilter && (
                <div data-name="dates" data-filter-size="custom">
                  <DateSelect
                    label="Term Date Range"
                    placeholder="Show All"
                    value={{
                      startDate: dateRange?.startsOn ?? null,
                      endDate: dateRange?.endsOn ?? null
                    }}
                    startFrom={dateRange?.startsOn ? new Date(dateRange.startsOn) : undefined}
                    useModal
                    useDefaultShortcuts
                    popoverDirection="down"
                    onChange={handleDateRangeChange}
                  />
                </div>
              )}
            </TableControls>
          )}
          renderFooter={() => (
            <TableFooter
              pagination={createFooterPagination(connection, {
                loading,
                pageCount: rows.length,
                totalCount: totalRowCount
              })}
              totalRowCount={totalRowCount}
            />
          )}
        />
      </div>
      <PersonSideSheet
        open={!!selectedParticipantPgaId}
        onClose={() => {
          setSelectedParticipantPgaId(null);
          setSelectedParticipantHighlightRowId(null);
        }}
        pgaId={selectedParticipantPgaId}
        customActionButtons={[
          {
            text: "View Committee History",
            variant: "outlined",
            onClick: () => {
              if (!selectedParticipantPgaId) return;
              const highlightParam = selectedParticipantHighlightRowId
                ? `?highlightRow=${encodeURIComponent(selectedParticipantHighlightRowId)}`
                : "";
              navigate(`/people/${selectedParticipantPgaId}/membership/committees${highlightParam}` as Path);
            }
          }
        ]}
      />
    </>
  );
}
